// Documents section inside the Vault. Lists every uploaded file with admin
// icons (download, re-analyze, discuss, delete) and opens a detail modal that
// shows the LLM-extracted summary, the structured facts, and the text chunks.
//
// "Discuss" stays inside the Vault: it opens a focused chat modal scoped to
// the document, so the user and the twin can collaboratively complete the
// findings. This is distinct from Converse (free-form Q&A) on purpose.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { useTwinName } from '../lib/twin.js';

interface DocumentFact { label: string; value: string; confidence?: number }

interface DocumentRow {
  id: string;
  createdAt: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: string;
  errorMessage: string | null;
  memoryId: string | null;
  summary: string | null;
  facts: DocumentFact[];
  analyzedAt: number | null;
  analysisModel: string | null;
  chunkCount: number;
  factCount: number;
}

interface DocumentChunk { id: string; idx: number; text: string; startChar: number; endChar: number }

interface DocumentDetail { attachment: DocumentRow; chunks: DocumentChunk[] }

interface ConvoMessage {
  id: string;
  role: 'visitor' | 'martin' | 'system';
  content: string;
  createdAt: number;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function statusChipKind(status: string): { label: string; color: string } {
  switch (status) {
    case 'analyzed': return { label: 'analyzed', color: 'text-emerald-300 border-emerald-300/30' };
    case 'analyzing': return { label: 'analyzing…', color: 'text-amber border-amber/40' };
    case 'processing': return { label: 'processing', color: 'text-amber border-amber/40' };
    case 'ready': return { label: 'ready', color: 'text-ink/60 border-ink/20' };
    case 'error': return { label: 'error', color: 'text-red-300 border-red-300/30' };
    default: return { label: status, color: 'text-ink/50 border-ink/15' };
  }
}

export function DocumentsView() {
  const qc = useQueryClient();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  // Document-scoped discussion: { docId, conversationId }
  const [discussing, setDiscussing] = useState<{ docId: string; conversationId: string } | null>(null);

  const { data, isLoading } = useQuery<{ documents: DocumentRow[] }>({
    queryKey: ['documents', 'list'],
    queryFn: () => api<{ documents: DocumentRow[] }>('/api/documents'),
    refetchInterval: (q) => {
      const rows = (q.state.data as { documents: DocumentRow[] } | undefined)?.documents ?? [];
      return rows.some((r) => r.status === 'processing' || r.status === 'analyzing') ? 4_000 : false;
    },
  });

  async function uploadFile(file: File) {
    setBusy(true); setUploadError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('visibility', 'private');
      fd.append('language', 'fr');
      const token = localStorage.getItem('mdai_token');
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['memory'] });
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setBusy(false);
    }
  }

  const docs = data?.documents ?? [];

  return (
    <div className="space-y-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          const f = e.dataTransfer.files?.[0]; if (f) void uploadFile(f);
        }}
        className={`px-6 py-5 rounded-2xl border-2 border-dashed transition-colors text-center ${
          drag ? 'border-amber bg-amber/[0.05]' : 'border-ink/15 hover:border-ink/30'
        } ${busy ? 'opacity-60' : ''}`}
      >
        <p className="font-serif italic text-ink/60 text-sm">
          {busy ? 'Uploading, extracting, chunking, analyzing…' : 'Drop a document here — PDF · DOCX · TXT · MD · HTML · GEDCOM — or'}
        </p>
        <label className="inline-block mt-2 px-4 py-2 rounded-full border border-ink/20 cursor-pointer text-[11.5px] text-ink/70 hover:text-ink hover:border-ink/40">
          Choose file
          <input type="file" accept=".pdf,.doc,.docx,.txt,.md,.markdown,.html,.htm,.ged,application/x-gedcom" className="hidden" disabled={busy} onChange={(e) => {
            const f = e.target.files?.[0]; if (f) void uploadFile(f); e.target.value = '';
          }} />
        </label>
        <p className="mt-2 text-[11px] text-ink/35">
          GEDCOM (.ged) → importe ton arbre généalogique : un souvenir d’héritage (visibilité famille)
        </p>
        {uploadError && <p className="mt-3 text-[11.5px] text-red-300">{uploadError}</p>}
      </div>

      {isLoading ? (
        <div className="text-ink/40 text-[12px]">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-center py-12 font-serif italic text-ink/40">
          No documents yet. Drop a PDF, a Word file or a note to get started.
        </div>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const chip = statusChipKind(d.status);
            return (
              <li key={d.id}
                onClick={() => setOpenDoc(d.id)}
                className="cursor-pointer border border-ink/10 hover:border-ink/25 rounded-xl p-4 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11.5px] text-ink/40 w-12">
                        {(d.mimeType.split('/')[1] ?? 'file').slice(0, 5)}
                      </span>
                      <span className="font-serif text-[16px] text-ink truncate">{d.fileName}</span>
                      <span className={`px-2 py-0.5 rounded-full border text-[11px] ${chip.color}`}>
                        {chip.label}
                      </span>
                    </div>
                    {d.summary && <p className="mt-2 font-serif text-[14px] text-ink/70 leading-relaxed line-clamp-2">{d.summary}</p>}
                    <div className="mt-2 flex items-center gap-3 text-[11.5px] text-ink/40">
                      <span>{formatBytes(d.sizeBytes)}</span>
                      <span>· {d.chunkCount} chunks</span>
                      <span>· {d.factCount} facts</span>
                      {d.memoryId && <span className="text-amber/70">· memory linked</span>}
                    </div>
                  </div>
                  <DocActions
                    doc={d}
                    onDiscuss={(conversationId) => setDiscussing({ docId: d.id, conversationId })}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {openDoc && (
        <DocumentDetailModal
          id={openDoc}
          onClose={() => setOpenDoc(null)}
          onDiscuss={(conversationId) => { setOpenDoc(null); setDiscussing({ docId: openDoc, conversationId }); }}
        />
      )}

      {discussing && (
        <DocumentDiscussionModal
          docId={discussing.docId}
          conversationId={discussing.conversationId}
          onClose={() => setDiscussing(null)}
        />
      )}
    </div>
  );
}

// ── Per-row actions ──────────────────────────────────────────────────────────

function DocActions({ doc, onDiscuss }: { doc: DocumentRow; onDiscuss: (conversationId: string) => void }) {
  const qc = useQueryClient();
  const analyze = useMutation({
    mutationFn: () => api(`/api/documents/${doc.id}/analyze`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/documents/${doc.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
  const discuss = useMutation({
    mutationFn: () => api<{ conversationId: string }>(`/api/documents/${doc.id}/discuss`, {
      method: 'POST', body: JSON.stringify({ language: 'fr' }),
    }),
    onSuccess: (r) => onDiscuss(r.conversationId),
  });

  const token = typeof window !== 'undefined' ? localStorage.getItem('mdai_token') : null;
  async function download() {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/documents/${doc.id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) { alert('Download failed.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = doc.fileName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
      <IconBtn title="Download" onClick={download}>↓</IconBtn>
      <IconBtn title="Re-analyze (chunks + facts)" onClick={() => analyze.mutate()} loading={analyze.isPending}>⟳</IconBtn>
      <IconBtn title="Discuss this document together" accent onClick={() => discuss.mutate()} loading={discuss.isPending}>💬</IconBtn>
      <IconBtn title="Delete" danger onClick={() => { if (confirm(`Delete "${doc.fileName}" and its chunks?`)) del.mutate(); }}>✕</IconBtn>
    </div>
  );
}

function IconBtn({ children, title, onClick, accent, danger, loading }: {
  children: React.ReactNode; title: string; onClick: () => void;
  accent?: boolean; danger?: boolean; loading?: boolean;
}) {
  const base = 'w-8 h-8 rounded-full border grid place-items-center text-sm transition-colors';
  const cls = loading
    ? 'border-ink/10 text-ink/30 cursor-wait'
    : danger
      ? 'border-red-300/25 text-red-300/70 hover:text-red-300 hover:border-red-300/50'
      : accent
        ? 'border-amber/40 text-amber/85 hover:text-amber hover:border-amber'
        : 'border-ink/15 text-ink/55 hover:text-ink hover:border-ink/40';
  return (
    <button title={title} onClick={onClick} disabled={loading} className={`${base} ${cls}`}>
      {loading ? '…' : children}
    </button>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function DocumentDetailModal({ id, onClose, onDiscuss }: { id: string; onClose: () => void; onDiscuss: (conversationId: string) => void }) {
  const { data, isLoading } = useQuery<DocumentDetail>({
    queryKey: ['documents', id],
    queryFn: () => api<DocumentDetail>(`/api/documents/${id}`),
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const discuss = useMutation({
    mutationFn: () => api<{ conversationId: string }>(`/api/documents/${id}/discuss`, {
      method: 'POST', body: JSON.stringify({ language: 'fr' }),
    }),
    onSuccess: (r) => onDiscuss(r.conversationId),
  });

  return (
    <div className="fixed inset-0 z-[110] bg-black/65 backdrop-blur-md grid place-items-center" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-[min(820px,94vw)] max-h-[90vh] overflow-y-auto bg-void border border-ink/15 rounded-2xl p-6 space-y-5">
        {isLoading || !data ? (
          <div className="text-ink/40 text-[12px]">Loading…</div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl text-ink">{data.attachment.fileName}</h3>
                <div className="mt-1 text-[12px] text-ink/45">
                  {formatBytes(data.attachment.sizeBytes)} · {data.chunks.length} chunks · {data.attachment.facts?.length ?? 0} facts
                  {data.attachment.analysisModel ? ` · ${data.attachment.analysisModel}` : ''}
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>
            </div>

            {data.attachment.summary && (
              <section className="space-y-2">
                <div className="text-[11px] text-ink/45">Summary</div>
                <p className="font-serif text-[15px] text-ink/85 leading-relaxed">{data.attachment.summary}</p>
              </section>
            )}

            {Array.isArray(data.attachment.facts) && data.attachment.facts.length > 0 && (
              <section className="space-y-2">
                <div className="text-[11px] text-ink/45">Extracted facts</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.attachment.facts.map((f, i) => (
                    <li key={i} className="px-3 py-2 rounded-lg border border-ink/10 bg-white/[0.015]">
                      <div className="text-[11px] text-ink/45">{f.label}</div>
                      <div className="font-serif text-[14px] text-ink/85 mt-0.5">{f.value}</div>
                      {typeof f.confidence === 'number' && (
                        <div className="font-mono text-[9px] text-ink/35 mt-1">confidence {f.confidence}%</div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-ink/45">Chunks</div>
                <button
                  onClick={() => discuss.mutate()}
                  disabled={discuss.isPending}
                  className="px-4 py-2 rounded-full border border-amber/40 text-amber/85 hover:text-amber hover:border-amber text-[11.5px]">
                  {discuss.isPending ? 'Opening…' : 'Discuss this document together →'}
                </button>
              </div>
              {data.chunks.length === 0 ? (
                <p className="font-serif italic text-ink/45 text-sm">
                  No text was extracted for this file. You can still discuss it, but the twin will have less to ground on.
                </p>
              ) : (
                <ul className="space-y-2 max-h-[40vh] overflow-y-auto">
                  {data.chunks.map((c) => (
                    <li key={c.id} className="px-3 py-2 rounded-md border border-ink/[0.07]">
                      <div className="text-[11px] text-ink/35 mb-1">
                        #{c.idx + 1} · chars {c.startChar}–{c.endChar}
                      </div>
                      <p className="font-serif text-[13.5px] text-ink/75 leading-relaxed whitespace-pre-wrap">{c.text}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

// ── Discussion modal ─────────────────────────────────────────────────────────
// An in-Vault chat scoped to the document. NOT Converse — Converse is for
// interrogating the twin in the abstract. Here the goal is the opposite: the
// twin proposes what it read, the user complements/corrects, and the
// conversation produces a richer understanding of the document.

function DocumentDiscussionModal({
  docId,
  conversationId,
  onClose,
}: { docId: string; conversationId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const twinName = useTwinName();
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Read the live LLM mode from the server so we can show the right banner.
  // 'mcp' means /send queues the turn for Claude Desktop; 'api' replies inline.
  const { data: cfg } = useQuery<{ llm?: { mode?: 'mcp' | 'api' } }>({
    queryKey: ['admin', 'config'],
    queryFn: () => api('/api/admin/config'),
    staleTime: 60_000,
  });
  const llmMode: 'mcp' | 'api' = cfg?.llm?.mode === 'api' ? 'api' : 'mcp';

  // Reference panel: small summary of the doc, kept visible so the user can
  // ground their replies.
  const { data: detail } = useQuery<DocumentDetail>({
    queryKey: ['documents', docId],
    queryFn: () => api<DocumentDetail>(`/api/documents/${docId}`),
  });

  const { data: msgsData } = useQuery<{ messages: ConvoMessage[] }>({
    queryKey: ['conversation', conversationId, 'messages'],
    queryFn: () => api(`/api/conversation/${conversationId}`),
    // Poll while the modal is open so turns recorded from Claude Desktop
    // (record_document_dialogue) show up live without needing to send.
    refetchInterval: 4_000,
  });
  const messages = (msgsData?.messages ?? []).filter((m) => m.role !== 'system');

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const send = useMutation({
    mutationFn: (content: string) =>
      api<{ stub?: boolean; pending?: boolean; via?: string }>('/api/conversation/send', {
        method: 'POST',
        body: JSON.stringify({ conversationId, content }),
      }),
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'messages'] });
    },
  });

  // Derive the "waiting on Claude Desktop" state from the transcript itself so
  // it survives closing/reopening the modal. If the last visible turn is from
  // Martin (visitor) AND we're in MCP mode, the twin owes us a reply.
  const lastRole = messages[messages.length - 1]?.role;
  const waitingMcp = llmMode === 'mcp' && lastRole === 'visitor';

  function submit() {
    const v = text.trim();
    if (!v || send.isPending) return;
    send.mutate(v);
  }

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-md grid place-items-center md:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full h-dvh rounded-none md:w-[min(1100px,96vw)] md:h-[min(820px,94vh)] md:rounded-2xl bg-void border border-ink/15 grid grid-rows-[auto_1fr_auto] overflow-hidden">

        {/* Header */}
        <header className="px-5 md:px-6 py-3 md:py-4 pt-safe md:pt-4 flex items-center justify-between border-b border-ink/10">
          <div className="min-w-0">
            <div className="text-[11.5px] text-ink/40 flex items-center gap-2">
              <span>Discuss together</span>
              <span className={`px-1.5 py-0.5 rounded-full border text-[9px] ${
                llmMode === 'api' ? 'border-emerald-300/30 text-emerald-300/80' : 'border-amber/40 text-amber/85'
              }`}>
                engine · {llmMode === 'api' ? 'API' : 'Claude Desktop'}
              </span>
            </div>
            <div className="font-serif text-lg text-ink truncate">
              {detail?.attachment.fileName ?? 'Document'}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-ink/15 grid place-items-center text-ink/50 hover:text-ink">✕</button>
        </header>

        {/* Persistent mode banner — MCP mode means replies happen out-of-band */}
        {llmMode === 'mcp' && (
          <div className={`px-6 py-2.5 border-b border-ink/10 font-serif italic text-[13px] leading-snug ${
            waitingMcp ? 'bg-amber/[0.07] text-amber/95' : 'bg-white/[0.02] text-ink/55'
          }`}>
            {waitingMcp ? (
              <>
                <strong className="not-italic text-[11.5px] mr-2">Waiting</strong>
                {twinName} replies through Claude Desktop. Ask it: <span className="font-mono text-amber">"drain pending dialogues"</span> — it will call <span className="font-mono text-amber">list_pending_dialogues</span> then <span className="font-mono text-amber">record_document_dialogue</span>. The reply will appear here within seconds.
              </>
            ) : (
              <>
                Engine is set to <strong className="not-italic">Claude Desktop</strong>. Send a message and the twin will reply via Claude Desktop. Switch to API in Settings → LLM for instant replies.
              </>
            )}
          </div>
        )}

        {/* Body: chat | reference (reference rail hides on mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] min-h-0">
          <div className="overflow-y-auto px-4 md:px-6 py-5 space-y-4">
            {messages.length === 0 ? (
              <div className="font-serif italic text-ink/50 text-sm">
                {twinName} is reading the document…
              </div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] px-4 py-3 rounded-2xl font-serif text-[15px] leading-relaxed ${
                  m.role === 'visitor'
                    ? 'bg-ink text-void rounded-br-sm'
                    : 'bg-white/[0.03] text-ink border border-ink/10 rounded-bl-sm'
                }`}>
                  {m.role === 'martin' && (
                    <div className="text-[11px] text-ink/45 mb-1">{twinName}</div>
                  )}
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {send.isPending && llmMode === 'api' && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl bg-white/[0.03] border border-ink/10">
                  <div className="text-[11.5px] text-ink/40">thinking…</div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Reference rail — hidden on mobile to give the chat full width */}
          <aside className="hidden md:block border-l border-ink/10 overflow-y-auto px-4 py-5 space-y-4 bg-white/[0.01]">
            {detail?.attachment.summary && (
              <section>
                <div className="text-[11px] text-ink/40 mb-1">Summary</div>
                <p className="font-serif italic text-[13px] text-ink/75 leading-relaxed">{detail.attachment.summary}</p>
              </section>
            )}
            {Array.isArray(detail?.attachment.facts) && detail!.attachment.facts.length > 0 && (
              <section>
                <div className="text-[11px] text-ink/40 mb-1.5">Facts</div>
                <ul className="space-y-1.5">
                  {detail!.attachment.facts.map((f, i) => (
                    <li key={i} className="text-[12px]">
                      <div className="text-[11px] text-ink/45">{f.label}</div>
                      <div className="font-serif text-ink/80">{f.value}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {detail?.attachment.summary == null && (
              <p className="font-serif italic text-[12px] text-ink/40">
                No structured analysis yet. The twin will reason from the raw chunks.
              </p>
            )}
          </aside>
        </div>

        {/* Composer */}
        <div className="border-t border-ink/10 px-4 py-3 pb-safe flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder={`Reply to ${twinName} — confirm, correct, or add what's missing.`}
            rows={2}
            className="flex-1 px-4 py-2.5 rounded-2xl border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none resize-none"
          />
          <button
            onClick={submit}
            disabled={!text.trim() || send.isPending}
            className="px-5 py-2.5 rounded-full bg-ink text-void text-[11.5px] hover:bg-particle disabled:opacity-30">
            {send.isPending ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
